import React, { useEffect, useRef, useState } from 'react';
import { textoPlazo, VERSION_AVISO } from './CierreHome';
import { waLink } from '../data/contacto';
import { evento, sesionId, VERSION_FLUJO } from '../lib/medicion';
import { VERSION_REGLAS, nivelSugerido } from '../data/planes';
import { FLOWS } from '../data/flows';

// Una pregunta del producto ya está contestada si su dato llegó con el recorrido (o, sin dato propio, si llegó lo que
// dejan puesto sus opciones: «para quién» en Agencia se elige en la home).
const contestada = (nodo, base) => (nodo.dataKey
  ? Boolean(base[nodo.dataKey])
  : nodo.options.every(o => o.fija && Object.keys(o.fija).every(k => base[k] !== undefined)));

// El chat de la home (spec v4, 03/11/2026): un ícono de diálogo abre una ventana tipo chatbot con
// un formulario corto. Reemplaza a «Otra cosa». Va por la misma función `contacto` que el cierre;
// el texto libre viaja solo con el contacto, nunca a la medición.
// `contexto` llega cuando lo abre el cierre (postularse, inscribirse): trae el asunto y los datos del recorrido,
// que viajan con el contacto como antes viajaban desde el formulario del cierre, y las preguntas del producto
// (`contexto.preguntas`, nodos de flows.js), que se contestan acá y no antes de ver la página (An, 05/11).
const ChatContacto = ({ abierto, setAbierto, texto = '', contexto = null }) => {
  const [ajustes, setAjustes] = useState(undefined);
  const [estado, setEstado] = useState('libre'); // libre | enviando | ok | error
  const [datos, setDatos] = useState({ necesidad: '', nombre: '', canal: 'whatsapp', dato: '', aviso: false });
  const [elegidas, setElegidas] = useState({}); // id de la pregunta → opción elegida
  const [otra, setOtra] = useState('');
  const primero = useRef(null);
  const boton = useRef(null);

  // Quien lo abre con un pedido (una opción sin camino propio, o postularse desde el cierre) deja el mensaje escrito.
  useEffect(() => {
    if (abierto && texto) setDatos(d => ({ ...d, necesidad: texto }));
  }, [abierto, texto]);
  useEffect(() => { setElegidas({}); setOtra(''); }, [contexto]);

  const base = contexto?.respuestas || {};
  const preguntas = (contexto?.preguntas || []).map(id => FLOWS[id]).filter(n => n?.options && !contestada(n, base));
  const juntas = Object.entries(elegidas).reduce((acc, [id, o]) => ({
    ...acc,
    ...(FLOWS[id].dataKey ? { [FLOWS[id].dataKey]: id === 'sistema_q1' && o.id === 'otra' && otra.trim() ? otra.trim() : o.label } : {}),
    ...o.fija,
  }), base);
  const elegir = (id, o) => {
    evento('respuesta', { pantalla: id, opcion: o.id });
    setElegidas(e => ({ ...e, [id]: o }));
  };

  useEffect(() => {
    if (!abierto) return undefined;
    evento('chat_abierto');
    primero.current?.focus();
    let vivo = true;
    fetch('/.netlify/functions/ajustes').then(r => r.json()).then(a => { if (vivo) setAjustes(a); }).catch(() => { if (vivo) setAjustes(null); });
    const esc = (e) => { if (e.key === 'Escape') { setAbierto(false); boton.current?.focus(); } };
    window.addEventListener('keydown', esc);
    return () => { vivo = false; window.removeEventListener('keydown', esc); };
  }, [abierto, setAbierto]);

  const enviar = async (e) => {
    e.preventDefault();
    if (estado === 'enviando' || !datos.aviso) return;
    setEstado('enviando');
    try {
      const r = await fetch('/.netlify/functions/contacto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre: datos.nombre, canal: datos.canal, dato_contacto: datos.dato, aviso_aceptado: true, version_aviso: VERSION_AVISO,
          servicio: contexto?.servicio || 'chat',
          respuestas: { ...juntas, nivel: nivelSugerido(juntas) || base.nivel || '', asunto: contexto?.asunto || '', necesidad: datos.necesidad.slice(0, 1500) },
          plan_sugerido: contexto?.plan_sugerido, plan_motivo: contexto?.plan_motivo,
          sesion_id: sesionId(), version_flujo: VERSION_FLUJO, version_reglas: VERSION_REGLAS,
        }),
      });
      if (!r.ok) throw new Error('fallo');
      evento('chat_enviado');
      setEstado('ok');
    } catch {
      evento('chat_error');
      setEstado('error');
    }
  };

  const pausada = ajustes && ajustes.pausada;
  const cambiar = (campo) => (e) => setDatos({ ...datos, [campo]: e.target.value });

  return (
    <div className="gh-chat">
      {abierto && (
        <div className="gh-chat__ventana" role="dialog" aria-labelledby="gh-chat-titulo">
          <div className="gh-chat__cabeza">
            <span id="gh-chat-titulo" className="gh-chat__titulo">{contexto?.asunto || '¿En qué te podemos ayudar?'}</span>
            <button type="button" className="gh-chat__cerrar" aria-label="Cerrar el chat" onClick={() => { setAbierto(false); boton.current?.focus(); }}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </div>
          <div className="gh-chat__cuerpo">
            {estado === 'ok' ? (
              <p className="gh-chat__gracias" role="status">Gracias, {datos.nombre.split(' ')[0]}. {textoPlazo(ajustes)}</p>
            ) : pausada ? (
              <p className="gh-chat__gracias">Por ahora no estamos recibiendo consultas por acá.{ajustes.mensaje ? ` ${ajustes.mensaje}` : ''}</p>
            ) : (
              // La estética del formulario del cierre (An, 04/11: «usá la estética de tu form para arreglar el del
              // chat»): campos llenos sin bordes, el canal como selector de dos, el aviso corto.
              <form className="gh-form" onSubmit={enviar}>
                <p className="gh-form__bajada">Antonela te escribe por donde prefieras.</p>
                {preguntas.map(n => (
                  <fieldset key={n.id} className="gh-form__pregunta">
                    <legend>{n.message}</legend>
                    <div role="radiogroup" aria-label={n.message}>
                      {n.options.map(o => (juntas.para === 'personal' && o.labelPersonal ? { ...o, label: o.labelPersonal } : o)).map(o => (
                        <button key={o.id} type="button" role="radio" aria-checked={elegidas[n.id]?.id === o.id} onClick={() => elegir(n.id, o)}>{o.label}</button>
                      ))}
                    </div>
                    {n.id === 'sistema_q1' && elegidas[n.id]?.id === 'otra' && (
                      <input className="gh-form__campo" maxLength={200} value={otra} placeholder={FLOWS.sistema_q1_otra.placeholder} aria-label={FLOWS.sistema_q1_otra.message} onChange={e => setOtra(e.target.value)} />
                    )}
                  </fieldset>
                ))}
                <textarea ref={primero} className="gh-form__campo" required maxLength={1500} rows={contexto ? 2 : 3} value={datos.necesidad} placeholder="Contanos en qué te podemos ayudar" aria-label="Tu mensaje" onChange={cambiar('necesidad')} />
                <input className="gh-form__campo" required maxLength={120} value={datos.nombre} autoComplete="name" placeholder="Tu nombre" aria-label="Tu nombre" onChange={cambiar('nombre')} />
                <div className="gh-form__canal" role="radiogroup" aria-label="Por dónde te escribimos">
                  {[['whatsapp', 'WhatsApp'], ['mail', 'Mail']].map(([id, t]) => (
                    <button key={id} type="button" role="radio" aria-checked={datos.canal === id} onClick={() => setDatos({ ...datos, canal: id })}>{t}</button>
                  ))}
                </div>
                <input className="gh-form__campo" required maxLength={160} value={datos.dato}
                  type={datos.canal === 'mail' ? 'email' : 'tel'} autoComplete={datos.canal === 'mail' ? 'email' : 'tel'}
                  placeholder={datos.canal === 'mail' ? 'Tu mail' : 'Tu número, con código de área'} aria-label={datos.canal === 'mail' ? 'Tu mail' : 'Tu número, con código de área'}
                  onChange={cambiar('dato')} />
                <label className="gh-form__aviso">
                  <input type="checkbox" checked={datos.aviso} onChange={e => setDatos({ ...datos, aviso: e.target.checked })} />
                  <span>Acepto que GUIA guarde lo que escribí y mi contacto solo para responderme. <a href="/privacidad" target="_blank" rel="noopener noreferrer">Privacidad</a></span>
                </label>
                {estado === 'error' && <p className="gh-form__error" role="alert">No pudimos guardar tu mensaje. Probá de nuevo o <a href={waLink(datos.necesidad)} target="_blank" rel="noopener noreferrer">escribinos por WhatsApp</a>.</p>}
                <button type="submit" className="guia-btn guia-btn--solido gh-form__enviar" disabled={!datos.aviso || estado === 'enviando'}>{estado === 'enviando' ? 'Enviando…' : 'Enviar'}</button>
              </form>
            )}
          </div>
        </div>
      )}
      <button
        ref={boton}
        type="button"
        className="gh-chat__boton"
        aria-expanded={abierto}
        aria-label={abierto ? 'Cerrar el chat' : 'Abrir el chat con GUIA'}
        onClick={() => setAbierto(a => !a)}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5h16v11H9l-5 4z" /><path d="M8 9h8M8 12h5" /></svg>
      </button>
    </div>
  );
};

export default ChatContacto;
